import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { resolveIcon } from '../components/AppShell/sidebarIcons';
import { t } from '../i18n/t';
import { useTabs } from './TabsContext';
import { useTabSwitcher } from './useTabSwitcher';
import type { LopTab } from './types';
import './LopTabSwitcher.css';

/**
 * Alt+Q MRU switcher popover (plan §3.5). Mounted ONCE in AppShell, next to
 * the existing g/e sidebar-search shortcut — it self-registers its own
 * keydown/keyup/blur listeners via useTabSwitcher() and renders nothing while
 * closed. Portalled to `document.body` (escapes any `overflow:hidden`
 * ancestor, same reasoning as SidebarRecent's popover) with a full-viewport
 * transparent backdrop that cancels on outside click.
 */
export function LopTabSwitcher() {
  const { tabs } = useTabs();
  const { open, orderedKeys, selectedKey, select, cancel, activateAndClose } = useTabSwitcher();

  const items = useMemo<LopTab[]>(() => {
    const byKey = new Map(tabs.map((tab) => [tab.key, tab] as const));
    const result: LopTab[] = [];
    for (const key of orderedKeys) {
      const tab = byKey.get(key);
      if (tab) result.push(tab);
    }
    return result;
    // orderedKeys is a stable snapshot from useTabSwitcher, re-derived only
    // when the popover (re)opens — `tabs` supplies live title/icon/dirty.
  }, [orderedKeys, tabs]);

  if (!open || items.length === 0) return null;

  return createPortal(
    <div className="lop-tab-switcher__backdrop" onMouseDown={cancel}>
      <div
        className="lop-tab-switcher"
        role="listbox"
        aria-label={t('tabs.switcherAriaLabel')}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {items.map((tab) => {
          const Icon = resolveIcon(tab.iconName);
          const isSelected = tab.key === selectedKey;
          return (
            <div
              key={tab.key}
              role="option"
              aria-selected={isSelected}
              className={
                'lop-tab-switcher__item' + (isSelected ? ' lop-tab-switcher__item--selected' : '')
              }
              onMouseEnter={() => select(tab.key)}
              onClick={() => activateAndClose(tab.key)}
            >
              {Icon && <Icon size={16} className="lop-tab-switcher__icon" aria-hidden="true" />}
              <span className="lop-tab-switcher__title">{tab.title}</span>
              {tab.dirty && <span className="lop-tab-switcher__dirty-dot" aria-hidden="true" />}
            </div>
          );
        })}
      </div>
    </div>,
    document.body,
  );
}
